import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useCart } from '../../contexts/CartContext';
import { ShoppingCart } from 'lucide-react';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();

  // Get price range if there are variations
  const getPriceDisplay = () => {
    if (product.variations && product.variations.length > 0) {
      const prices = product.variations.map(v => v.price).filter(p => p > 0);
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        if (minPrice !== maxPrice) {
          return `£${minPrice.toFixed(2)} - £${maxPrice.toFixed(2)}`;
        }
        return `£${minPrice.toFixed(2)}`;
      }
    }
    return `£${product.price.toFixed(2)}`;
  };

  const hasVariations = product.variations && product.variations.length > 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only add to cart if there are no variations
    // Products with variations need to go to detail page to select option
    if (!hasVariations) {
      addToCart(product);
    }
  };

  return (
    <Link 
      to={`/products/${product.slug}`}
      className="group block bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800 hover:border-amber-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/10"
    >
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-zinc-800">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.badge && (
            <Badge 
              className={`${
                product.badge === 'NEW' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-amber-500 text-black'
              } font-bold`}
            >
              {product.badge}
            </Badge>
          )}
          {hasVariations && (
            <Badge className="bg-blue-500/90 text-white font-medium text-xs">
              {product.variations.length} Options
            </Badge>
          )}
        </div>

        {/* Quick add button */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <Button
            onClick={handleAddToCart}
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {hasVariations ? 'View Options' : 'Add to Cart'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Category */}
        <p className="text-amber-500 text-xs font-medium uppercase tracking-wide mb-1">
          {product.category.replace(/-/g, ' ')}
        </p>
        
        {/* Name */}
        <h3 className="text-white font-semibold text-lg mb-1 group-hover:text-amber-500 transition-colors">
          {product.name}
        </h3>
        
        {/* Description */}
        <p className="text-zinc-400 text-sm mb-3 line-clamp-1">
          {product.shortDescription}
        </p>

        {/* Variations preview */}
        {hasVariations && (
          <div className="flex flex-wrap gap-1 mb-3">
            {product.variations.slice(0, 3).map((variation, index) => (
              <span 
                key={index}
                className="text-xs px-2 py-1 bg-zinc-800 text-zinc-400 rounded-md"
              >
                {variation.name}
              </span>
            ))}
            {product.variations.length > 3 && (
              <span className="text-xs px-2 py-1 bg-zinc-800 text-zinc-500 rounded-md">
                +{product.variations.length - 3} more
              </span>
            )}
          </div>
        )}
        
        {/* Price */}
        <p className="text-amber-500 font-bold text-xl">
          {hasVariations && <span className="text-sm font-normal text-zinc-500 mr-1">from</span>}
          {getPriceDisplay()}
        </p>
      </div>
    </Link>
  );
};

export default ProductCard;
